import type { CSSProperties } from 'react';
import { Tooltip } from 'antd';
import type { IndicatorResultNode } from '@/api/assessment';

/**
 * 单指标行 — 对位原型 docs/prototypes/ops-compliance-dashboard.html .r 块。
 *
 * 视觉契约：
 *   网格 48px / 1.6fr / 92px / 1.5fr / 84px → 条款编号 / 名称 / 要求 / 当前值(+进度条) / 状态 pill
 *   severity → token 映射（0 hex；ρ-1 教训）：
 *     ok      → success bg + success text
 *     fail    → danger bg-alt + danger-2 text
 *     pending → warning bg + warning-2 text
 *
 * 子行（sub2=true）：缩进 + 浅底；不显示条款编号格。
 * OR 父行（orParent=true）：浅绿底，提示 "OR 满足任一即可"。
 *
 * 注意：父行 passed 由后端 evaluate() 算（assess.py:78 any(k.passed)），前端不重算。
 */

export interface IndicatorRowProps {
  data: IndicatorResultNode;
  /** 条款编号（中文一二三...）；子行/无编号则不传 */
  clause?: string;
  /** 子行（OR 复合 children）— 缩进 + 浅底，不重画 pill 边距 */
  sub?: boolean;
  /** OR 复合父行 — 浅绿底高亮 */
  orParent?: boolean;
}

const SEVERITY_PILL: Record<
  IndicatorResultNode['severity'],
  { bg: string; color: string; label: string }
> = {
  ok: {
    bg: 'var(--color-success-bg-alt)',
    color: 'var(--color-success)',
    label: '达标',
  },
  fail: {
    bg: 'var(--color-danger-bg-alt)',
    color: 'var(--color-danger-2)',
    label: '未达标',
  },
  pending: {
    bg: 'var(--color-warning-bg)',
    color: 'var(--color-warning-2)',
    label: '待确认',
  },
};

const BAR_STROKE: Record<IndicatorResultNode['severity'], string> = {
  ok: 'var(--color-success-2)',
  fail: 'var(--color-danger-3)',
  pending: 'var(--color-warning)',
};

/** 把 actual / required 都视为数字时算百分比；否则返 null（不画进度条）。 */
function computePercent(
  actual: number | string | null,
  required: number | string | null,
): number | null {
  const a = typeof actual === 'number' ? actual : Number(actual);
  const r = typeof required === 'number' ? required : Number(required);
  if (!Number.isFinite(a) || !Number.isFinite(r) || r <= 0) return null;
  const pct = (a / r) * 100;
  // 上限 100（达标条满格），下限 0
  return Math.max(0, Math.min(100, Math.round(pct)));
}

/**
 * 该指标的当前值/阈值是否是百分比 — 仅看主名(去括号补充),
 * 匹配 占比 / 比率 / 比例 / 各种"X率"。
 * 排除 "规模 / 数 / 人数 / 面积" 这类计数/面积单位。
 */
function isPercentMetric(name: string): boolean {
  const main = name.replace(/[(（][^)）]*[)）]/g, '');
  return /占比|比率|比例|增长率|覆盖率|毕业率|通过率|留存率|参与率/.test(main);
}

/** 数字+%格式化;非数字字符串/null 保持原样('—'/'1:5'/'OR' 等)。 */
function formatPercentValue(v: number | string | null): string {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${n}%`;
}

export function IndicatorRow({
  data,
  clause,
  sub = false,
  orParent = false,
}: IndicatorRowProps) {
  const pill = SEVERITY_PILL[data.severity];
  const isManual = data.actual == null && data.passed == null;
  const percent = isManual ? null : computePercent(data.actual, data.required);
  const isPercent = isPercentMetric(data.name);

  const rowStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '48px 1.6fr 92px 1.5fr 84px',
    gap: 10,
    alignItems: 'center',
    padding: '11px 14px',
    borderBottom: '1px solid var(--color-line)',
    fontSize: sub ? 'var(--font-size-sm)' : 'var(--font-size-base)',
    background: orParent
      ? 'var(--color-success-bg)'
      : sub
        ? 'var(--color-bg-page-warm)'
        : data.severity === 'fail'
          ? 'var(--color-danger-bg-alt)'
          : 'transparent',
  };

  return (
    <div style={rowStyle}>
      {/* 条款编号 */}
      <span
        style={{
          fontWeight: 'var(--font-weight-semi)' as CSSProperties['fontWeight'],
          color: 'var(--color-text-secondary)',
          textAlign: 'center',
        }}
      >
        {clause ?? ''}
      </span>

      {/* 指标名称（+ OR 副标 + Sprint 50 政策依据 Tooltip + 自动/人工徽章） */}
      <div
        style={{
          fontWeight: 'var(--font-weight-medium)' as CSSProperties['fontWeight'],
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {sub ? '　' : ''}
          {data.policy_ref ? (
            <Tooltip title={data.policy_ref} placement="topLeft">
              <span style={{ borderBottom: '1px dotted var(--color-text-tertiary)', cursor: 'help' }}>
                {data.name}
              </span>
            </Tooltip>
          ) : (
            <span>{data.name}</span>
          )}
          {/* Sprint 50:自动 / 人工徽章 — 仅父行或扁平叶子节点显示;子条件行(sub)不重复 */}
          {!sub && (
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                padding: '1px 6px',
                borderRadius: 'var(--radius-pill)',
                background:
                  data.auto_compute === false
                    ? 'var(--color-warning-bg)'
                    : 'var(--color-info-bg, #e6f4ff)',
                color:
                  data.auto_compute === false
                    ? 'var(--color-warning-2)'
                    : 'var(--color-info, #1677ff)',
                fontWeight: 'var(--font-weight-regular)' as CSSProperties['fontWeight'],
                whiteSpace: 'nowrap',
              }}
            >
              {data.auto_compute === false ? '✋ 待人工' : '🤖 自动算'}
            </span>
          )}
        </span>
        {orParent && (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-tertiary)',
              fontWeight: 'var(--font-weight-regular)' as CSSProperties['fontWeight'],
            }}
          >
            A 或 B 满足其一即可（OR）
          </span>
        )}
        {/* 政策原文短引用兜底:无 Tooltip 用户也能看到一行小字(限 sub 行不显,避免噪音) */}
        {!sub && data.policy_ref && (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-tertiary)',
              fontWeight: 'var(--font-weight-regular)' as CSSProperties['fontWeight'],
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
            title={data.policy_ref}
          >
            依据:{data.policy_ref}
          </span>
        )}
      </div>

      {/* 要求 / 阈值 */}
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {data.required == null
          ? '—'
          : isPercent
            ? formatPercentValue(data.required)
            : String(data.required)}
      </span>

      {/* 当前值 + 进度条 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {percent != null && (
          <div
            style={{
              flex: 1,
              minWidth: 40,
              height: 6,
              borderRadius: 4,
              background: 'var(--color-border-soft)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${percent}%`,
                height: '100%',
                borderRadius: 4,
                background: BAR_STROKE[data.severity],
              }}
            />
          </div>
        )}
        {isManual ? (
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-tertiary)',
              fontStyle: 'normal',
            }}
          >
            {data.note || '人工确认'}
          </span>
        ) : (
          <b
            style={{
              fontWeight: 'var(--font-weight-semi)' as CSSProperties['fontWeight'],
              whiteSpace: 'nowrap',
              color:
                data.severity === 'fail'
                  ? 'var(--color-danger-2)'
                  : 'var(--color-text-primary)',
            }}
          >
            {data.actual == null
              ? '—'
              : isPercent
                ? formatPercentValue(data.actual)
                : String(data.actual)}
          </b>
        )}
        {!isManual && data.note && (
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-success)',
              marginLeft: 4,
            }}
          >
            {data.note}
          </span>
        )}
      </div>

      {/* 状态 pill */}
      <span
        style={{
          justifySelf: 'end',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)' as CSSProperties['fontWeight'],
          padding: '4px 10px',
          borderRadius: 'var(--radius-pill)',
          whiteSpace: 'nowrap',
          background: pill.bg,
          color: pill.color,
        }}
      >
        {pill.label}
      </span>
    </div>
  );
}
